import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Parser from "html-react-parser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperclip, faTimes } from "@fortawesome/free-solid-svg-icons";
import ReactTooltip from "react-tooltip";
import * as Sentry from "@sentry/react";
import Button from "../../../../shared/button/Button";
import Sending from "../../../../shared/loaders/Sending";
import ModalThankYou from "../../../../shared/loaders/ModalThankYou";
import {
  Form,
  Label,
  Field,
  TextInput,
  ButtonsWrapper,
  FileInputLabel,
  FileInput,
  Filename,
  RemoveAttachment,
  Error,
} from "../styles/interview-styles";
import InterviewDatePicker from "../../../../InterviewDatePicker";

const applicationActionMap = {
  "phone-interview": "telephone_interview_confirmed",
  "first-interview": "first_interview_confirmed",
  "second-interview": "second_interview_confirmed",
};

const displayNameMap = {
  "phone-interview": "phone",
  "first-interview": "first",
  "second-interview": "second",
};

class Confirm extends Component {
  constructor(props) {
    super(props);
    this.attachmentRef = React.createRef();

    this.state = {
      formLoading: false,
      successMessageActive: false,
      message: "",
      attachment: "",
      fileAttached: false,
      errors: null,
      actionDate: null,
      actionAt: null,
      actionEndAt: null,
    };
  }

  showSuccessMessage = () => {
    this.setState({ successMessageActive: true });
  };

  handleSubmit = (e) => {
    e.preventDefault();
    this.setState({ formLoading: true });
    const { message, attachment, actionAt, actionEndAt } = this.state;
    const {
      cardData,
      closeActionModalAfterSubmit,
      fetchKanbanData,
      interviewType,
    } = this.props;
    const { updateStatusLink } = cardData;
    const url = updateStatusLink;

    const formData = new FormData();

    formData.append(
      "application_action[action]",
      applicationActionMap[interviewType]
    );
    formData.append("application_action[message]", message);
    formData.append("application_action[attachment]", attachment);
    formData.append("application_action[action_at]", actionAt);
    formData.append("application_action[action_end_at]", actionEndAt);

    const payload = {
      url,
      method: "post",
      headers: { "Content-Type": "multipart/form-data" },
      data: formData,
    };

    axios(payload)
      .then(() => {
        this.setState({ formLoading: false, errors: null });
        this.showSuccessMessage();
        setTimeout(() => {
          closeActionModalAfterSubmit();
          fetchKanbanData({ loading: false, updating: true });
        }, 2000);
      })
      .catch((error) => {
        this.setState({
          errors: error.response.data.errors,
          formLoading: false,
        });
        Sentry.captureException(error);
      });
  };

  handleMessage = (e) => {
    this.setState({ message: e.target.value });
  };

  handleAttachment = (e) => {
    this.setState({ attachment: e.target.files[0], fileAttached: true });
  };

  removeAttachment = () => {
    const node = this.attachmentRef.current;

    this.setState({ attachment: "", fileAttached: false });
    node.value = null;
  };

  handleDateChange = (date) => {
    const { actionAt, actionEndAt } = this.state;

    this.setState({
      actionDate: date,
      actionAt: this.updateDateExcludingTime(actionAt, date),
      actionEndAt: this.updateDateExcludingTime(actionEndAt, date),
    });
  };

  updateDateExcludingTime = (dateToUpdate, date) => {
    if (dateToUpdate === null || date === null) return null;

    const newDate = new Date(dateToUpdate);
    newDate.setDate(date.getDate());

    return newDate;
  };

  handleStartTimeChange = (date) => {
    const actionAt = this.actionDateWithCustomTime(date);

    this.setState({ actionAt });
  };

  handleEndTimeChange = (date) => {
    const actionEndAt = this.actionDateWithCustomTime(date);

    this.setState({ actionEndAt });
  };

  actionDateWithCustomTime = (date) => {
    const { actionDate } = this.state;
    if (actionDate === null) return null;

    const newDate = new Date(actionDate);

    newDate.setHours(date.getHours());
    newDate.setMinutes(date.getMinutes());
    newDate.setSeconds(date.getSeconds());

    return newDate;
  };

  render() {
    const {
      errors,
      message,
      fileAttached,
      successMessageActive,
      formLoading,
      actionDate,
      actionAt,
      actionEndAt,
    } = this.state;
    const { candidate, handleCancel, interviewType } = this.props;

    const capitalisedDisplayName =
      displayNameMap[interviewType].charAt(0).toUpperCase() +
      displayNameMap[interviewType].slice(1);

    return (
      <Fragment>
        {formLoading && <Sending white />}
        {successMessageActive && (
          <ModalThankYou
            text={`${capitalisedDisplayName} interview confirmed`}
          />
        )}
        <Form onSubmit={this.handleSubmit}>
          <Label className="animated fadeInUp delay-1">
            You&apos;re about to <strong>confirm</strong>{" "}
            {displayNameMap[interviewType]} interview with{" "}
            <strong>{candidate}</strong>. <br />
            Please select <strong>date</strong>, <strong>time</strong> and add
            your <strong>message</strong>.
          </Label>
          <Field>
            <InterviewDatePicker
              actionDate={actionDate}
              handleDateChange={this.handleDateChange}
              actionAt={actionAt}
              handleStartTimeChange={this.handleStartTimeChange}
              actionEndAt={actionEndAt}
              handleEndTimeChange={this.handleEndTimeChange}
            />
            {errors && errors.action_at && (
              <Error className="animated bounceIn">
                Start time {errors.action_at}.
              </Error>
            )}
            {errors && errors.action_end_at && (
              <Error className="animated bounceIn">
                End time {errors.action_end_at}.
              </Error>
            )}
          </Field>
          <Field className="animated fadeInUp delay-3">
            <TextInput
              value={message}
              placeholder="Please add your message..."
              onChange={this.handleMessage}
            />
            {errors && errors.message && (
              <Error className="animated bounceIn">
                Message {errors.message}.
              </Error>
            )}
          </Field>
          <Field className="animated fadeInUp delay-4">
            <FileInputLabel htmlFor="file" fileAttached={fileAttached}>
              <FontAwesomeIcon icon={faPaperclip} />
            </FileInputLabel>
            <FileInput
              type="file"
              name="file"
              id="file"
              ref={this.attachmentRef}
              onChange={this.handleAttachment}
            />
            <Filename>
              {fileAttached
                ? `${this.attachmentRef.current.files[0].name.replace(
                    "C:\\fakepath\\",
                    ""
                  )}`
                : Parser("Attach a file <span>(optional)</span>")}
            </Filename>
            {fileAttached && (
              <Fragment>
                <ReactTooltip
                  html
                  place="left"
                  type="dark"
                  effect="solid"
                  aria-haspopup="true"
                  id="remove-attachment-tooltip"
                />
                <RemoveAttachment
                  onClick={this.removeAttachment}
                  data-tip="Remove this attachment"
                  data-for="remove-attachment-tooltip"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </RemoveAttachment>
              </Fragment>
            )}
          </Field>
          <ButtonsWrapper className="animated fadeInUp delay-4">
            <Button
              secondary
              buttonName="Cancel"
              onClickAction={handleCancel}
            />
            <Button primary buttonName="Confirm Interview" type="submit" />
          </ButtonsWrapper>
        </Form>
      </Fragment>
    );
  }
}

Confirm.propTypes = {
  handleCancel: PropTypes.func.isRequired,
  candidate: PropTypes.string.isRequired,
  closeActionModalAfterSubmit: PropTypes.func.isRequired,
  cardData: PropTypes.shape({
    candidate: PropTypes.string,
    agent: PropTypes.string,
    updateStatusLink: PropTypes.string,
  }).isRequired,
  fetchKanbanData: PropTypes.func.isRequired,
  interviewType: PropTypes.string.isRequired,
};

export default Confirm;
